import React, { useState } from "react";
import { API, copyText } from "@/lib/api";
import { toast } from "sonner";
import { X, DownloadSimple, ShareNetwork, FacebookLogo, InstagramLogo, LinkSimple } from "@phosphor-icons/react";

const cardUrl = (gameId, size) => `${API}/potg/${gameId}/card.png?size=${size}`;
const shareUrl = (gameId) => `${API}/share/potg/${gameId}`;

export default function PotgShareModal({ card, onClose }) {
  const [size, setSize] = useState("square");
  const [busy, setBusy] = useState(false);
  const name = (card.player_name || "player").replace(/\s+/g, "-").toLowerCase();

  const download = async () => {
    setBusy(true);
    try {
      const res = await fetch(cardUrl(card.game_id, size));
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `potg-${name}-${size}.png`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Card downloaded");
    } catch { toast.error("Could not download the card"); }
    finally { setBusy(false); }
  };

  const nativeShare = async () => {
    setBusy(true);
    try {
      const res = await fetch(cardUrl(card.game_id, size));
      const blob = await res.blob();
      const file = new File([blob], `potg-${name}.png`, { type: "image/png" });
      const text = `${card.player_name} — Player of the Game! ${card.pts || 0} PTS · ${card.reb || 0} REB · ${card.ast || 0} AST · ${card.blk || 0} BLK`;
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "Player of the Game", text });
        return;
      }
      if (navigator.share) { await navigator.share({ title: "Player of the Game", text, url: shareUrl(card.game_id) }); return; }
      const ok = await copyText(shareUrl(card.game_id));
      toast[ok ? "success" : "error"](ok ? "Share link copied" : "Could not share");
    } catch (e) {
      if (e && e.name === "AbortError") return;
      toast.error("Sharing isn't available on this device — try Download instead");
    } finally { setBusy(false); }
  };

  const openFacebook = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl(card.game_id))}`, "_blank", "noopener,width=640,height=640");
  };

  const copyLink = async () => {
    const ok = await copyText(shareUrl(card.game_id));
    toast[ok ? "success" : "error"](ok ? "Share link copied" : "Could not copy link");
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={onClose} data-testid="potg-share-modal">
      <div className="bg-[#121212] border border-white/10 w-full max-w-lg max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <h3 className="font-display text-xl uppercase flex items-center gap-2"><ShareNetwork size={20} weight="fill" className="text-[#FF5722]" /> Share Card</h3>
          <button onClick={onClose} data-testid="potg-share-close" className="text-white/50 hover:text-white"><X size={20} /></button>
        </div>

        <div className="p-5 space-y-4">
          {/* format toggle */}
          <div className="flex gap-2" data-testid="potg-share-size-toggle">
            <button onClick={() => setSize("square")} data-testid="potg-share-size-square"
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-xs uppercase font-bold tracking-wide border transition-colors ${size === "square" ? "bg-[#FF5722] text-black border-[#FF5722]" : "border-white/15 text-white/60 hover:text-white"}`}>
              <InstagramLogo size={16} weight="bold" /> Instagram (1:1)
            </button>
            <button onClick={() => setSize("wide")} data-testid="potg-share-size-wide"
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-xs uppercase font-bold tracking-wide border transition-colors ${size === "wide" ? "bg-[#FF5722] text-black border-[#FF5722]" : "border-white/15 text-white/60 hover:text-white"}`}>
              <FacebookLogo size={16} weight="bold" /> Facebook (16:9)
            </button>
          </div>

          {/* preview */}
          <div className="bg-[#0A0A0A] border border-white/10 p-2 flex items-center justify-center">
            <img key={size} src={cardUrl(card.game_id, size)} alt="Player of the Game share card" data-testid="potg-share-preview"
              className={`w-full ${size === "square" ? "max-w-sm" : ""} object-contain`} />
          </div>

          <p className="text-xs text-white/40 leading-relaxed">
            Instagram doesn't allow direct web posting — tap <span className="text-white/70 font-semibold">Download</span> then upload the image to your story or feed. For Facebook, use <span className="text-white/70 font-semibold">Share to Facebook</span> to post with a live preview.
          </p>

          <div className="grid grid-cols-2 gap-2">
            <button onClick={download} disabled={busy} data-testid="potg-share-download" className="flex items-center justify-center gap-2 bg-[#FF5722] hover:bg-[#FF7043] disabled:opacity-50 text-black font-bold uppercase tracking-wide text-sm py-3 transition-colors">
              <DownloadSimple size={18} weight="bold" /> Download
            </button>
            <button onClick={nativeShare} disabled={busy} data-testid="potg-share-native" className="flex items-center justify-center gap-2 border border-white/20 hover:border-white text-white/80 font-bold uppercase tracking-wide text-sm py-3 transition-colors">
              <ShareNetwork size={18} weight="bold" /> Share
            </button>
            <button onClick={openFacebook} data-testid="potg-share-facebook" className="flex items-center justify-center gap-2 border border-[#1877F2]/60 text-[#4a90ff] hover:bg-[#1877F2]/10 font-bold uppercase tracking-wide text-sm py-3 transition-colors">
              <FacebookLogo size={18} weight="fill" /> Facebook
            </button>
            <button onClick={copyLink} data-testid="potg-share-copy" className="flex items-center justify-center gap-2 border border-white/20 hover:border-white text-white/80 font-bold uppercase tracking-wide text-sm py-3 transition-colors">
              <LinkSimple size={18} weight="bold" /> Copy Link
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
